import Bluebird from 'bluebird'
import { compact, map, sum } from 'lodash'
import JSZip from 'jszip'
import * as pdfjsLib from 'pdfjs-dist'

import store from '../../store'
import { singlePageDocOfSize } from './document'
import { doLayout } from './layout'
import { renderItem } from './renderer'
import { preloadFonts } from './fonts/font_loader'

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).href

async function pdfBlobToPng(blobUrl) {
  const pdf = await pdfjsLib.getDocument(blobUrl).promise
  const page = await pdf.getPage(1)
  // 4× scale ≈ 288 DPI (PDF base is 72 pt/in)
  const viewport = page.getViewport({ scale: 4 })
  const canvas = document.createElement('canvas')
  canvas.width = Math.round(viewport.width)
  canvas.height = Math.round(viewport.height)
  await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise
  return new Promise(resolve => canvas.toBlob(resolve, 'image/png'))
}


// Limit concurrent renders to avoid overwhelming font loading, image
// downloads, and jsPDF's global event list when many cards mount at once.
const MAX_CONCURRENT_RENDERS = 2
let activeRenders = 0
const renderQueue = []

function enqueueRender(fn, priority = 0) {
  let entry
  const promise = new Promise((resolve, reject) => {
    entry = { fn, resolve, reject, priority, cancelled: false }
    renderQueue.push(entry)
    renderQueue.sort((a, b) => a.priority - b.priority)
    drainRenderQueue()
  })
  // Attach a cancel handle so callers can remove this render from the queue
  // without it consuming a concurrency slot or producing a result.
  promise.cancel = () => { entry.cancelled = true }
  return promise
}

function drainRenderQueue() {
  while (activeRenders < MAX_CONCURRENT_RENDERS && renderQueue.length > 0) {
    const entry = renderQueue.shift()
    if (entry.cancelled) {
      entry.resolve(null)   // null signals cancellation to the caller
      continue              // don't consume a concurrency slot
    }
    activeRenders++
    entry.fn().then(entry.resolve, entry.reject).finally(() => {
      activeRenders--
      drainRenderQueue()
    })
  }
}

const publicApi = {
  renderItemToPdf(game, component, item, template, priority = 0) {
    return enqueueRender(() => {
      let doc,
        fontsToLoad = store.getters.allFontsForComponent(component)

      fontsToLoad = collapseVariantsIntoFamilies(fontsToLoad)

      return store.dispatch("startExportItem")
        .then(() => preloadFonts(fontsToLoad))
        .then(() => doc = singlePageDocOfSize(template.size))
        .then(() => renderItem(doc, game, component, item, template.size, 0, 1)
          .then(results => {
            results.issues.forEach(issue => store.dispatch("exportItemStatusUpdate", issue))
          }))
        .then(() => doc.output('bloburi'))
        .finally(() => store.dispatch("finishExportItem"))
    })
  },

  async renderGameToPng(game) {
    store.dispatch("startGameExport")
    try {
      const components = store.getters.findAllPrintableGameComponents(game)
      const fontsForGame = collapseVariantsIntoFamilies(store.getters.allFontsForGame(game))
      const totalItems = sum(map(components, c => store.getters.getComponentItems(c).length))

      store.dispatch("exportGameStatusUpdate", `Preloading fonts...`)
      await preloadFonts(fontsForGame)

      store.dispatch("exportGameStatusUpdate", `Rendering ${totalItems} card(s) across ${components.length} component(s) at 288 DPI`)

      const zip = new JSZip()
      let done = 0

      await Bluebird.each(components, async (component) => {
        const template = store.getters.findTemplate(component.templateId)
        if (!template) return
        const items = store.getters.getComponentItems(component)
        const folder = zip.folder(component.title || 'Component')

        store.dispatch("exportGameStatusUpdate", `"${component.title}" — rendering ${items.length} card(s)`)

        await Bluebird.each(items, async (item, i) => {
          store.dispatch("exportGameStatusInlineUpdate", i + 1)
          const blobUri = await this.renderItemToPdf(game, component, item, template)
          if (!blobUri) return
          const pngBlob = await pdfBlobToPng(blobUri)
          folder.file(`card-${String(i + 1).padStart(3, '0')}.png`, pngBlob)
          done++
        })
      })

      const filename = `${game.title || 'Game'} - PNG Export.zip`
      store.dispatch("exportGameStatusUpdate", `Building "${filename}"...`)
      const zipBlob = await zip.generateAsync({ type: 'blob' })

      const a = document.createElement('a')
      a.href = URL.createObjectURL(zipBlob)
      a.download = filename
      a.click()
      URL.revokeObjectURL(a.href)

      store.dispatch("exportGameStatusUpdate", `Done — ${done} card(s) exported.`)
    } finally {
      store.dispatch("finishGameExport")
    }
  },

  renderGameToPdf(game) {
    store.dispatch("startGameExport")

    const
      components = store.getters.findAllPrintableGameComponents(game),
      componentSizes = compact(components.map((component) => {
        const template = store.getters.findTemplate(component.templateId)

        return {
          size: template.size,
          name: component.id,
          quantity: store.getters.getComponentItems(component).length
        }
      })),
      printSettings = store.getters.getPrintSettings,
      numComponents = componentSizes.length,
      numItems = sum(map(componentSizes, "quantity")),
      fontsForGame = collapseVariantsIntoFamilies(store.getters.allFontsForGame(game))

    let doc, itemLocations, guideLocations, numPages

    store.dispatch("exportGameStatusUpdate", `Preloading Fonts:\n- ${map(fontsForGame, "family").join('\n- ')}`)

    // must preload fonts before creating a doc
    return preloadFonts(fontsForGame)

      // now get the doc and lay out all the components
      .then(() => {
        store.dispatch("exportGameStatusUpdate", "Laying out document with components and print settings")
        const layoutObject = doLayout(componentSizes, printSettings)
        doc = layoutObject.doc
        itemLocations = layoutObject.itemLocations
        guideLocations = layoutObject.guideLocations
        numPages = doc.getNumberOfPages()

        store.dispatch("exportGameStatusUpdate", `Layout Complete: ${numPages} pages, ${numComponents} components, ${numItems} items`)
      })

      // Render all items into the PDF via the given itemLocations
      .then(() => Bluebird.each(components, (component) => {
        const items = store.getters.getComponentItems(component)
        store.dispatch("exportGameStatusUpdate", `Starting "${component.title}" (${items.length} items)`)

        return Bluebird.each(items, (item, itemIndex, totalItems) => {
          store.dispatch("exportGameStatusInlineUpdate", itemIndex+1)
          let parentDimensions = itemLocations[component.id].shift()

          return renderItem(doc, game, component, item, parentDimensions, itemIndex, totalItems)
            .then(results => {
              results.issues.forEach(issue => store.dispatch("exportGameStatusUpdate", issue))
            })
        })

        // TODO: cut-lines support by processing guideLocations here

      }).then(() => {
        const filenameOfDownload = `${game.title || "Game"}.pdf`
        store.dispatch("exportGameStatusUpdate", `Finished render, exporting file: "${filenameOfDownload}"`)
        return doc.save(filenameOfDownload)

      }).finally(() => store.dispatch("finishGameExport") ))
  },
}

function collapseVariantsIntoFamilies(fonts) {
  return map(fonts, (font) => {
    return {
      family: `${font.family}-${font.variant}`,
      variant: "normal",
      location: font.location
    }
  })
}

export default publicApi
